/**
 * SUPPORTED CURRENCIES for the Marketplace.
 *
 * Order matters — this is the order presented in the picker. USD is
 * the default for the seller side (both because most of our sellers
 * are US-based and because it's the pre-existing implicit currency
 * on all destash items before this feature landed).
 *
 * `symbol` is what we render before the price (e.g. `€25.00`). Some
 * currencies use suffix conventions (`45.00 lei`) — the shared
 * `formatPrice` helper below encodes those quirks per code.
 */

export interface CurrencyInfo {
  code: string;
  symbol: string;
  label: string;
}

export const SUPPORTED_CURRENCIES: CurrencyInfo[] = [
  { code: 'USD', symbol: '$', label: 'US Dollar (USD)' },
  { code: 'CAD', symbol: 'CA$', label: 'Canadian Dollar (CAD)' },
  { code: 'GBP', symbol: '£', label: 'British Pound (GBP)' },
  { code: 'EUR', symbol: '€', label: 'Euro (EUR)' },
  { code: 'AUD', symbol: 'AU$', label: 'Australian Dollar (AUD)' },
  { code: 'JPY', symbol: '¥', label: 'Japanese Yen (JPY)' },
  { code: 'TRY', symbol: '₺', label: 'Turkish Lira (TRY)' },
  { code: 'RON', symbol: 'lei', label: 'Romanian Leu (RON)' },
];

const CURRENCY_BY_CODE: Record<string, CurrencyInfo> = SUPPORTED_CURRENCIES.reduce(
  (acc, c) => {
    acc[c.code] = c;
    return acc;
  },
  {} as Record<string, CurrencyInfo>
);

export function getCurrencyInfo(code: string | undefined | null): CurrencyInfo {
  if (!code) return CURRENCY_BY_CODE.USD;
  return CURRENCY_BY_CODE[code] ?? CURRENCY_BY_CODE.USD;
}

/**
 * Format a numeric price with the correct currency symbol placement.
 *
 * Conventions:
 *   USD/CAD/GBP/AUD/EUR/JPY → symbol before amount ($25.00, €25.00)
 *   TRY → symbol before amount (₺25.00) — matches how most of the app
 *     ecosystem in Turkey renders it in web contexts
 *   RON → amount before symbol with a space (25.00 lei) — matches
 *     Romanian bank/receipt convention
 *
 * JPY has no decimals in practice; we still show `.00` to keep prices
 * comparable — collectors' polish listings are rarely priced in
 * fractions of a yen anyway.
 */
export function formatPrice(
  amount: number | undefined | null,
  currency: string | undefined | null
): string {
  const info = getCurrencyInfo(currency);
  const value = typeof amount === 'number' && !isNaN(amount) ? amount : 0;
  const decimals = info.code === 'JPY' ? 0 : 2;
  const formatted = value.toFixed(decimals);
  if (info.code === 'RON') return `${formatted} lei`;
  return `${info.symbol}${formatted}`;
}
